import React from "react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import videoThumbnailImg from "assets/images/video-thumbnail.png";
import "./ImageCarousel.css"; // We'll create this CSS file
import CheveronLeftCircleIcon from "assets/icons/HeroIcons/CheveronLeftCircleIcon";
import CheveronRightCircleIcon from "assets/icons/HeroIcons/CheveronRightCircleIcon";

interface ImageCarouselProps {
  images: any[];
  currentIndex: number;
  showExifData: boolean;
  onImageChange: (index: number) => void;
  onImageClick: (item: any) => void;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  currentIndex,
  showExifData,
  onImageChange,
  onImageClick,
}) => {
  const hasMultipleImages = images.length > 1;

  const handleThumbnailClick = (index: number) => {
    onImageChange(index);
  };

  // Function to get the visible thumbnail range (3 images)
  const getVisibleThumbnails = () => {
    const totalImages = images.length;
    if (totalImages <= 3) {
      return { start: 0, end: totalImages - 1 };
    }

    let start = Math.max(0, currentIndex - 1);
    let end = Math.min(totalImages - 1, start + 2);

    // Adjust if we're at the end
    if (end === totalImages - 1 && end - start < 2) {
      start = Math.max(0, end - 2);
    }

    return { start, end };
  };

  const { start, end } = getVisibleThumbnails();
  const visibleThumbnails = images.slice(start, end + 1);

  if (images.length === 0) {
    return <div>No images available</div>;
  }

  return (
    <div
      className={`${showExifData ? "w-[730px]" : "w-[730px]"} flex flex-col`}
    >
      <div className="relative image-carousel-slider">
        <Carousel
          selectedItem={currentIndex}
          onChange={onImageChange}
          showThumbs={false}
          showStatus={false}
          showIndicators={false}
          infiniteLoop={hasMultipleImages}
          useKeyboardArrows
          swipeable
          emulateTouch
          renderArrowNext={(clickHandler, hasNext) => (
            <button
              className={`absolute bottom-8 right-8 z-50 ${
                currentIndex < images.length - 1
                  ? "cursor-pointer block"
                  : "opacity-50 cursor-not-allowed hidden"
              }`}
              onClick={
                currentIndex < images.length - 1 ? clickHandler : undefined
              }
              type="button"
              disabled={currentIndex >= images.length - 1}
            >
              <CheveronRightCircleIcon
                className="text-primary"
                fill="primary"
              />
            </button>
          )}
          renderArrowPrev={(clickHandler, hasPrev) => (
            <button
              className={`absolute bottom-8 left-8 z-50 ${
                currentIndex > 0
                  ? "cursor-pointer block"
                  : "opacity-50 cursor-not-allowed hidden"
              }`}
              onClick={currentIndex > 0 ? clickHandler : undefined}
              type="button"
              disabled={currentIndex <= 0}
            >
              <CheveronLeftCircleIcon className="text-primary" fill="primary" />
            </button>
          )}
        >
          {images?.map((image, index) => {
            return (
              <div
                onClick={() => {
                  onImageClick(image);
                }}
                key={index}
                className="relative cursor-pointer"
              >
                {image.dataType === "image" ? (
                  <img
                    src={image.url}
                    className={`w-full max-h-[446px] cursor-pointer rounded-2xl object-cover`}
                    alt={`Observation ${index + 1}`}
                  />
                ) : image.dataType === "video" ? (
                  <img
                    src={videoThumbnailImg}
                    className={`w-full ${showExifData ? "h-[350px]" : "h-[400px]"} cursor-pointer rounded-lg object-cover`}
                    alt={`Video thumbnail ${index + 1}`}
                  />
                ) : null}
              </div>
            );
          })}
        </Carousel>

        {/* Thumbnail Images - Shows sliding window of 3 images */}
        {images.length > 0 && (
          <div className="thumbnail-container flex items-center justify-center gap-x-4 mt-8">
            {visibleThumbnails.map((image, index) => {
              const actualIndex = start + index;
              return (
                <div
                  key={actualIndex}
                  onClick={() => handleThumbnailClick(actualIndex)}
                  className={`thumbnail-item ${
                    actualIndex === currentIndex ? "active" : ""
                  }`}
                >
                  {image.dataType === "image" ? (
                    <img src={image.url} alt={`Thumbnail ${actualIndex + 1}`} />
                  ) : image.dataType === "video" ? (
                    <img
                      src={videoThumbnailImg}
                      alt={`Video thumbnail ${actualIndex + 1}`}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageCarousel;
